import React, { useState, KeyboardEvent } from 'react';
import { X } from 'lucide-react';

export interface EmailChipInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  label?: string;
  helperText?: string;
  validator?: (email: string) => boolean;
  errorMessage?: string;
}

const DEFAULT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (e: string) => DEFAULT_EMAIL_RE.test(e.trim());

export const EmailChipInput: React.FC<EmailChipInputProps> = ({
  value,
  onChange,
  placeholder = 'Add email and press Enter',
  label,
  helperText,
  validator,
  errorMessage = 'Not a valid email',
}) => {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const validate = validator || isValidEmail;

  const commit = () => {
    const candidate = draft.trim().replace(/,$/, '');
    if (!candidate) return;
    if (!validate(candidate)) {
      setError(errorMessage);
      return;
    }
    if (value.includes(candidate)) {
      setError('Already added');
      setDraft('');
      return;
    }
    onChange([...value, candidate]);
    setDraft('');
    setError(null);
  };

  const remove = (idx: number) => {
    onChange(value.filter((_, i) => i !== idx));
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      remove(value.length - 1);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <label
          style={{
            fontSize: '13px',
            fontWeight: 500,
            color: 'var(--text)',
          }}
        >
          {label}
        </label>
      )}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
          padding: '8px 10px',
          background: 'var(--panel-strong, var(--panel))',
          border: '1px solid var(--stroke)',
          borderRadius: 'var(--radius-md, 8px)',
          minHeight: 40,
        }}
      >
        {value.map((email, idx) => (
          <span
            key={`${email}-${idx}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              background: 'var(--primary-50, rgba(33,119,255,0.10))',
              color: 'var(--primary-600, #2177FF)',
              border: '1px solid var(--primary-200, rgba(33,119,255,0.25))',
              borderRadius: '9999px',
              padding: '3px 4px 3px 10px',
              fontSize: '13px',
              lineHeight: 1.2,
            }}
          >
            {email}
            <button
              type="button"
              onClick={() => remove(idx)}
              aria-label={`Remove ${email}`}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                padding: 2,
              }}
            >
              <X size={12} />
            </button>
          </span>
        ))}
        <input
          type="email"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={onKey}
          onBlur={commit}
          placeholder={value.length === 0 ? placeholder : ''}
          style={{
            flex: 1,
            minWidth: 140,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            color: 'var(--text)',
            fontSize: '14px',
            padding: '4px 2px',
          }}
        />
      </div>
      {(helperText || error) && (
        <span
          style={{
            fontSize: '12px',
            color: error ? 'var(--danger, #dc2626)' : 'var(--text-secondary)',
          }}
        >
          {error || helperText}
        </span>
      )}
    </div>
  );
};

export default EmailChipInput;
